import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { StorefrontApiError, storefront } from "../api/storefront";
import type { AuthMeResponse } from "../api/types";

/**
 * Auth store — passwordless magic-link.
 *
 * Flow:
 *   1. UI calls requestMagicLink(email) → backend sends email with /auth/verify?token=…
 *   2. On click of that link, verify route fires verifyToken(token) → backend
 *      sets httpOnly cookie + returns profile_id.
 *   3. On cold load App.vue calls fetchMe() to restore session.
 */
export const useAuthStore = defineStore("auth", () => {
  const me = ref<AuthMeResponse | null>(null);
  const loading = ref(false);
  const lastError = ref<string | null>(null);
  const linkSentTo = ref<string | null>(null);
  // Guest sessions share profileId=null. An explicit cookie boundary still
  // invalidates private menu state and responses from the previous buyer.
  const buyerGeneration = ref(0);

  // Анонимная гостевая сессия — НЕ «залогинен» (показываем форму регистрации).
  // Защитно проверяем и anonymous: даже если бэкенд ещё старый и вернул
  // authenticated:true для анон-сессии, витрина не считает гостя вошедшим.
  const isAuthed = computed(
    () => me.value?.authenticated === true && me.value?.anonymous !== true,
  );
  const profileId = computed(() => me.value?.profile_id ?? null);

  async function fetchMe(): Promise<void> {
    loading.value = true;
    try {
      me.value = await storefront.auth.me();
    } catch (err) {
      // Anonymous session → treat as not authed, not as error.
      if (err instanceof StorefrontApiError && err.statusCode < 500) {
        me.value = { authenticated: false };
      } else {
        lastError.value = (err as Error).message;
        me.value = { authenticated: false };
      }
    } finally {
      loading.value = false;
    }
  }

  async function requestMagicLink(email: string): Promise<void> {
    lastError.value = null;
    loading.value = true;
    try {
      await storefront.auth.requestMagicLink(email);
      linkSentTo.value = email;
    } catch (err) {
      lastError.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function verifyToken(token: string): Promise<boolean> {
    lastError.value = null;
    loading.value = true;
    try {
      await storefront.auth.verifyToken(token);
      await fetchMe();
      return true;
    } catch (err) {
      lastError.value = (err as Error).message;
      return false;
    } finally {
      loading.value = false;
    }
  }

  // §5.11 Ф4 — регистрация/вход по телефону (SMS-код).
  async function requestSmsCode(input: { phone: string; captchaToken?: string }): Promise<{ devCode?: string; cooldownSeconds?: number }> {
    lastError.value = null;
    loading.value = true;
    try {
      const res = await storefront.auth.requestSmsCode({ phone: input.phone, captcha_token: input.captchaToken });
      linkSentTo.value = input.phone;
      return { devCode: res.dev_code, cooldownSeconds: res.cooldown_seconds };
    } catch (err) {
      lastError.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function verifySmsCode(input: { phone: string; code: string; name?: string; birthday?: string; ref_code?: string }): Promise<boolean> {
    lastError.value = null;
    loading.value = true;
    try {
      await storefront.auth.verifySmsCode(input);
      await fetchMe();
      return true;
    } catch (err) {
      lastError.value = (err as Error).message;
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await storefront.auth.logout();
      buyerGeneration.value++;
    } catch {
      // best-effort
    }
    me.value = { authenticated: false };
    linkSentTo.value = null;
  }

  function reset(): void {
    me.value = null;
    loading.value = false;
    lastError.value = null;
    linkSentTo.value = null;
  }

  return {
    me,
    loading,
    lastError,
    linkSentTo,
    isAuthed,
    profileId,
    buyerGeneration,
    fetchMe,
    requestMagicLink,
    verifyToken,
    requestSmsCode,
    verifySmsCode,
    logout,
    reset,
  };
});
